/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { LitElement, html } from 'lit';
import { property } from 'lit/decorators.js';
import EarthFilled16 from '@carbon/web-components/es/icons/earth--filled/16.js';
import FocusMixin from '@carbon/web-components/es/globals/mixins/focus.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import { FOOTER_SIZE } from './footer';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import styles from './footer.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { prefix, stablePrefix: c4dPrefix } = settings;

/**
 * Locale button.
 *
 * @element c4d-locale-button
 */
@customElement(`${c4dPrefix}-locale-button`)
class C4DLocaleButton extends StableSelectorMixin(FocusMixin(LitElement)) {
  /**
   * Button label for accessibility.
   */
  @property()
  buttonLabel = 'Select geographic area';

  /**
   * Size property to apply different styles.
   */
  @property()
  size = FOOTER_SIZE.REGULAR;

  /**
   * The shadow slot this locale button should be in.
   */
  @property({ reflect: true })
  slot = 'locale-button';

  render() {
    const { buttonLabel } = this;
    return html`
      <button
        part="locale-button"
        id="button"
        class="${prefix}--btn ${prefix}--btn--tertiary ${c4dPrefix}--locale-btn"
        aria-label="${ifDefined(buttonLabel)}">
        <slot></slot>
        ${EarthFilled16()}
      </button>
    `;
  }

  static get stableSelector() {
    return `${c4dPrefix}--locale-btn`;
  }

  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };
  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

export default C4DLocaleButton;
